import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Input, Space, Spin, Switch, message } from 'antd';
import { CommerceNotice, CommerceNoticeStatusCode, ImageRef } from '@gooduncles/gu-app-schema';

import { useAuth } from 'src/stores/auth-context';
import { useCommerceNoticeById } from 'src/hooks/useCommerceNotice';
import { createCommerceNotice, updateCommerceNotice } from 'src/utils/firebase/firebase-short-cut';

import CommerceNoticeEditor from 'src/components/CommerceNotice/CommerceNoticeEditor/CommerceNoticeEditor';
import { resolveContentImageUrls } from 'src/components/CommerceNotice/CommerceNoticeEditor/TiptapImageExtension';

const CommerceNoticeFormPage: FC = () => {
  const { id: noticeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!noticeId;

  const { notice, isLoading } = useCommerceNoticeById(noticeId);
  const [form] = Form.useForm();
  const [content, setContent] = useState<Record<string, any> | null>(null);
  const [images, setImages] = useState<Record<string, ImageRef>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (notice) {
      form.setFieldsValue({
        title: notice.title,
        category: notice.category,
        pinned: notice.pinned,
      });
      const noticeImages = notice.images ?? {};
      setContent(resolveContentImageUrls(notice.content, noticeImages));
      setImages(noticeImages);
    }
  }, [notice, form]);

  const handleSave = useCallback(
    async (status: CommerceNoticeStatusCode) => {
      if (!user) return;

      try {
        await form.validateFields();
        setSubmitting(true);

        const values = form.getFieldsValue();
        const now = new Date().toISOString();

        const data: CommerceNotice = {
          title: values.title,
          content: content ?? {},
          status,
          category: values.category ?? '',
          pinned: values.pinned ?? false,
          authorId: user._id,
          authorName: user.userName ?? user.email ?? '',
          publishedAt: status === CommerceNoticeStatusCode.PUBLISHED ? now : notice?.publishedAt ?? null,
          viewCount: notice?.viewCount ?? 0,
          images,
        };

        const isDraft = status === CommerceNoticeStatusCode.DRAFT;

        if (isEdit && noticeId) {
          await updateCommerceNotice(noticeId, data);
          message.success(isDraft ? '임시저장되었습니다.' : '공지사항이 수정되었습니다.');
        } else {
          const newId = await createCommerceNotice(data);
          message.success(isDraft ? '임시저장되었습니다.' : '공지사항이 저장되었습니다.');
          if (isDraft && newId) {
            navigate(`/commerce-notice/edit/${newId}`, { replace: true });
            return;
          }
        }

        if (!isDraft) {
          navigate('/commerce-notice/list');
        }
      } catch (error) {
        console.error('Save failed:', error);
        message.error('저장에 실패했습니다.');
      } finally {
        setSubmitting(false);
      }
    },
    [user, form, content, images, isEdit, noticeId, notice, navigate],
  );

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <Form form={form} layout='vertical'>
        <Form.Item label='제목' name='title' rules={[{ required: true, message: '제목을 입력해주세요' }]}>
          <Input placeholder='공지사항 제목' />
        </Form.Item>

        <Form.Item label='카테고리' name='category'>
          <Input placeholder='카테고리 (선택)' />
        </Form.Item>

        <Form.Item label='상단 고정' name='pinned' valuePropName='checked'>
          <Switch />
        </Form.Item>

        <Form.Item label='본문' required>
          <CommerceNoticeEditor
            content={content}
            images={images}
            onContentChange={setContent}
            onImagesChange={setImages}
            noticeId={noticeId ?? 'new'}
            authorId={user?._id ?? ''}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={() => navigate('/commerce-notice/list')}>취소</Button>
            <Button loading={submitting} onClick={() => handleSave(CommerceNoticeStatusCode.DRAFT)}>
              임시저장
            </Button>
            <Button type='primary' loading={submitting} onClick={() => handleSave(CommerceNoticeStatusCode.PUBLISHED)}>
              게시
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CommerceNoticeFormPage;
